import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';

import {
    GetAdminData,
    UpdateAdminConfig,
    UpdateAdminOverlay,
    UploadAsset,
    GetThemes,
    ActivateTheme,
    DeleteTheme,
    getExportExcelUrl
} from '../../../../utilities/DL/PersonalSurfer';
import { resolveAssetUrl, maskPhoneNumber } from '../../../../utilities/BL/PersonalSurfer';
import SignalRService from '../../../../utilities/WS/SignalRService';

import { PersonalSurferMetricsRow } from './PersonalSurferMetricsRow';
import { PersonalSurferThemeModal } from './PersonalSurferThemeModal';
import { PersonalSurferResetModal } from './PersonalSurferResetModal';
import { PersonalSurferDangerModal } from './PersonalSurferDangerModal';

/**
 * COMPONENTE: PersonalSurferBoard
 * [HU-03] Consola Administrativa Touch-Friendly para Esquivador Personal
 */
export const PersonalSurferBoard = ({
    getOutOfTheDynamic = () => {},
    roomId: propRoomId = null
}) => {
    // -------------------------------------------------------------------------
    // 1. RESOLUCIÓN DE IDENTIFICADOR DE SALA
    // -------------------------------------------------------------------------
    const initialRoomId = useMemo(() => {
        if (propRoomId) return propRoomId;
        if (typeof window !== 'undefined') {
            const searchParams = new URLSearchParams(window.location.search);
            const urlRoomId = searchParams.get('roomId') || searchParams.get('room');
            if (urlRoomId) return urlRoomId;
            const stored = localStorage.getItem('active_room_id') || localStorage.getItem('interaktia_room_id') || localStorage.getItem('roomId') || localStorage.getItem('room');
            if (stored) return stored;
        }
        return '';
    }, [propRoomId]);

    const [resolvedRoomId, setResolvedRoomId] = useState('');
    const roomId = propRoomId || resolvedRoomId || initialRoomId || '';

    // -------------------------------------------------------------------------
    // 2. ESTADOS PRINCIPALES DE DATOS
    // -------------------------------------------------------------------------
    const [isLoading, setIsLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [themes, setThemes] = useState([]);
    const [activeTheme, setActiveTheme] = useState(null);
    const [metrics, setMetrics] = useState({});
    const [leaderboard, setLeaderboard] = useState([]);
    const [participants, setParticipants] = useState([]);

    // -------------------------------------------------------------------------
    // 3. ESTADOS DE FORMULARIO - ACORDEÓN 1: OVERLAY
    // -------------------------------------------------------------------------
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [overlayMode, setOverlayMode] = useState('invitation');
    const [overlayQrCount, setOverlayQrCount] = useState(1);
    const [overlayOffsetY, setOverlayOffsetY] = useState(0);
    const [overlayBanner1536Url, setOverlayBanner1536Url] = useState('');
    const [overlayBanner1920Url, setOverlayBanner1920Url] = useState('');
    const [isSavingOverlay, setIsSavingOverlay] = useState(false);

    // -------------------------------------------------------------------------
    // 4. ESTADOS DE FORMULARIO - ACORDEÓN 2: PARÁMETROS DE JUEGO
    // -------------------------------------------------------------------------
    const [isGameActive, setIsGameActive] = useState(true);
    const [winMode, setWinMode] = useState('sudden_death');
    const [timeLimitSeconds, setTimeLimitSeconds] = useState(30);
    const [baseSpeed, setBaseSpeed] = useState(5);
    const [speedPreset, setSpeedPreset] = useState('normal');
    const [difficultyMode, setDifficultyMode] = useState('progressive');
    const [pointsPerTick, setPointsPerTick] = useState(10);
    const [pointsPerCoin, setPointsPerCoin] = useState(100);
    const [pointsPerBonus, setPointsPerBonus] = useState(500);
    const [penaltyPerHit, setPenaltyPerHit] = useState(150);
    const [allowSharedDevice, setAllowSharedDevice] = useState(true);
    const [selectedThemeId, setSelectedThemeId] = useState('');
    const [isSavingConfig, setIsSavingConfig] = useState(false);

    // -------------------------------------------------------------------------
    // 5. ESTADOS DE TABLA DE PARTICIPANTES, FILTROS Y PII
    // -------------------------------------------------------------------------
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [hidePii, setHidePii] = useState(true);

    // -------------------------------------------------------------------------
    // 6. ESTADOS DE MODALES Y TOASTS
    // -------------------------------------------------------------------------
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [editingTheme, setEditingTheme] = useState(null);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [resetTargetParticipant, setResetTargetParticipant] = useState(null);
    const [isDangerModalOpen, setIsDangerModalOpen] = useState(false);

    const [toast, setToast] = useState({ visible: false, type: 'info', message: '' });
    const [wsStatus, setWsStatus] = useState('connecting');

    const triggerToast = useCallback((type, message) => {
        setToast({ visible: true, type, message });
        setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 4000);
    }, []);

    // -------------------------------------------------------------------------
    // 7. CARGA DE DATOS CONSOLIDADOS
    // -------------------------------------------------------------------------
    const loadAdminData = useCallback(async (isSilent = false) => {
        if (!roomId) return;
        if (!isSilent) setIsLoading(true);

        try {
            const res = await GetAdminData(roomId);
            if (res.success && res.data) {
                const data = res.data;

                if (data.config) {
                    setConfig(data.config);
                    setIsGameActive(Boolean(data.config.isGameActive));
                    setWinMode(data.config.winMode || 'sudden_death');
                    setTimeLimitSeconds(Number(data.config.timeLimitSeconds) || 30);
                    setBaseSpeed(Number(data.config.baseSpeed) || 5);
                    setSpeedPreset(data.config.speedPreset || 'normal');
                    setDifficultyMode(data.config.difficultyMode || 'progressive');
                    setPointsPerTick(Number(data.config.pointsPerTick) || 10);
                    setPointsPerCoin(Number(data.config.pointsPerCoin) || 100);
                    setPointsPerBonus(Number(data.config.pointsPerBonus) || 500);
                    setPenaltyPerHit(Number(data.config.penaltyPerHit) || 150);
                    setAllowSharedDevice(data.config.allowSharedDevice !== undefined ? Boolean(data.config.allowSharedDevice) : true);

                    setOverlayVisible(Boolean(data.config.overlayVisible));
                    setOverlayMode(data.config.overlayMode || 'invitation');
                    setOverlayQrCount(Number(data.config.overlayQrCount) || 1);
                    setOverlayOffsetY(Number(data.config.overlayOffsetY) || 0);
                    setOverlayBanner1536Url(data.config.overlayBanner1536Url || '');
                    setOverlayBanner1920Url(data.config.overlayBanner1920Url || '');

                    if (data.config.activeThemeId) {
                        setSelectedThemeId(data.config.activeThemeId);
                    }
                    if (data.config.activeTheme) {
                        setActiveTheme(data.config.activeTheme);
                    }
                }

                if (Array.isArray(data.themes)) {
                    setThemes(data.themes);
                }
                if (data.metrics) {
                    setMetrics(data.metrics);
                }
                if (Array.isArray(data.leaderboard)) {
                    setLeaderboard(data.leaderboard);
                }
                if (Array.isArray(data.participants)) {
                    setParticipants(data.participants);
                }
            } else {
                triggerToast('error', res.message || 'Error al cargar datos administrativos.');
            }
        } catch (err) {
            triggerToast('error', 'Error de conexión con el servidor.');
        } finally {
            if (!isSilent) setIsLoading(false);
        }
    }, [roomId, triggerToast]);

    const loadAdminDataRef = useRef(loadAdminData);
    useEffect(() => {
        loadAdminDataRef.current = loadAdminData;
    }, [loadAdminData]);

    useEffect(() => {
        loadAdminData();
    }, [loadAdminData]);

    // -------------------------------------------------------------------------
    // 8. CONEXIÓN SIGNALR EN TIEMPO REAL
    // -------------------------------------------------------------------------
    useEffect(() => {
        let isMounted = true;

        const handleNewParticipant = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            if (!data) return;
            console.log('[PersonalSurferBoard] WS: newParticipant:', data);

            setParticipants((prev) => [data, ...prev.filter((p) => p.id !== data.id)]);
            setMetrics((prev) => ({
                ...prev,
                formCompletesCount: (prev.formCompletesCount || 0) + 1
            }));
            triggerToast('info', `📝 Nuevo registro: ${data.fullName || 'Aficionado'}`);
        };

        const handleNewPlay = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            if (!data) return;
            console.log('[PersonalSurferBoard] WS: newPlay:', data);

            setParticipants((prev) =>
                prev.map((p) => (p.id === data.participantId ? { ...p, ...data } : p))
            );

            if (data.leaderboard) setLeaderboard(data.leaderboard);
            if (data.metrics) setMetrics(data.metrics);

            triggerToast('success', `🏎️ Carrera finalizada: ${data.fullName || 'Aficionado'} (${data.score || 0} pts - Pos #${data.rankingPosition || 1})`);
        };

        const handleConfigUpdated = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            if (!data) return;
            console.log('[PersonalSurferBoard] WS: configUpdated:', data);
            setConfig(data);
            if (data.isGameActive !== undefined) setIsGameActive(Boolean(data.isGameActive));
            if (data.activeTheme) setActiveTheme(data.activeTheme);
        };

        const handleOverlayUpdated = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            if (!data) return;
            console.log('[PersonalSurferBoard] WS: overlayUpdated:', data);
            if (data.overlayVisible !== undefined) setOverlayVisible(Boolean(data.overlayVisible));
            if (data.overlayMode) setOverlayMode(data.overlayMode);
            if (data.overlayQrCount !== undefined) setOverlayQrCount(Number(data.overlayQrCount));
            if (data.overlayOffsetY !== undefined) setOverlayOffsetY(Number(data.overlayOffsetY));
        };

        const handleTelemetryUpdated = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            if (!data) return;
            setMetrics(data);
        };

        const handleReset = () => {
            console.log('[PersonalSurferBoard] WS: reset recibido');
            loadAdminDataRef.current(true);
            triggerToast('warning', 'La sala ha sido reiniciada.');
        };

        SignalRService.on('personalSurfer:newParticipant', handleNewParticipant);
        SignalRService.on('personalSurfer:newPlay', handleNewPlay);
        SignalRService.on('personalSurfer:configUpdated', handleConfigUpdated);
        SignalRService.on('personalSurfer:overlayUpdated', handleOverlayUpdated);
        SignalRService.on('personalSurfer:telemetryUpdated', handleTelemetryUpdated);
        SignalRService.on('personalSurfer:participantReset', () => loadAdminDataRef.current(true));
        SignalRService.on('personalSurfer:reset', handleReset);
        SignalRService.on('reconnected', () => { if (isMounted) setWsStatus('connected'); loadAdminDataRef.current(true); });
        SignalRService.on('reconnecting', () => { if (isMounted) setWsStatus('reconnecting'); });
        SignalRService.on('close', () => { if (isMounted) setWsStatus('closed'); });

        setWsStatus('connected');

        return () => {
            isMounted = false;
            SignalRService.off('personalSurfer:newParticipant', handleNewParticipant);
            SignalRService.off('personalSurfer:newPlay', handleNewPlay);
            SignalRService.off('personalSurfer:configUpdated', handleConfigUpdated);
            SignalRService.off('personalSurfer:overlayUpdated', handleOverlayUpdated);
            SignalRService.off('personalSurfer:telemetryUpdated', handleTelemetryUpdated);
            SignalRService.off('personalSurfer:participantReset');
            SignalRService.off('personalSurfer:reset', handleReset);
        };
    }, [triggerToast]);

    // -------------------------------------------------------------------------
    // 9. ACCIONES DE GUARDADO DE CONFIGURACIÓN Y OVERLAY
    // -------------------------------------------------------------------------
    const handleSaveOverlay = async (e) => {
        e.preventDefault();
        setIsSavingOverlay(true);
        try {
            const payload = {
                roomId,
                overlayVisible,
                overlayMode,
                overlayQrCount: Number(overlayQrCount),
                overlayOffsetY: Number(overlayOffsetY),
                overlayBanner1536Url: overlayBanner1536Url || null,
                overlayBanner1920Url: overlayBanner1920Url || null
            };
            const res = await UpdateAdminOverlay(payload, roomId);
            if (res.success) {
                triggerToast('success', 'Overlay actualizado en tiempo real.');
            } else {
                triggerToast('error', res.message || 'Error al actualizar el overlay.');
            }
        } catch {
            triggerToast('error', 'Error de red.');
        } finally {
            setIsSavingOverlay(false);
        }
    };

    const handleSaveConfig = async (e) => {
        e.preventDefault();
        setIsSavingConfig(true);
        try {
            const payload = {
                roomId,
                isGameActive,
                activeThemeId: selectedThemeId || null,
                winMode,
                timeLimitSeconds: Number(timeLimitSeconds),
                baseSpeed: Number(baseSpeed),
                speedPreset,
                difficultyMode,
                pointsPerTick: Number(pointsPerTick),
                pointsPerCoin: Number(pointsPerCoin),
                pointsPerBonus: Number(pointsPerBonus),
                penaltyPerHit: Number(penaltyPerHit),
                allowSharedDevice
            };
            const res = await UpdateAdminConfig(payload, roomId);
            if (res.success) {
                triggerToast('success', 'Parámetros de juego guardados.');
            } else {
                triggerToast('error', res.message || 'Error al guardar la configuración.');
            }
        } catch {
            triggerToast('error', 'Error de red.');
        } finally {
            setIsSavingConfig(false);
        }
    };

    const handleBannerUpload = async (e, type) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const res = await UploadAsset(file, type, roomId);
            if (res.success && res.data?.url) {
                if (type === 'banner1536') setOverlayBanner1536Url(res.data.url);
                if (type === 'banner1920') setOverlayBanner1920Url(res.data.url);
                triggerToast('success', 'Banner subido y optimizado con éxito.');
            } else {
                triggerToast('error', res.message || 'Error al subir el banner.');
            }
        } catch {
            triggerToast('error', 'Error al procesar el archivo.');
        }
    };

    const handleActivateTheme = async (themeId) => {
        try {
            const res = await ActivateTheme(themeId, roomId);
            if (res.success) {
                setSelectedThemeId(themeId);
                triggerToast('success', 'Tema activado en vivo.');
                loadAdminDataRef.current(true);
            } else {
                triggerToast('error', res.message || 'Error al activar tema.');
            }
        } catch {
            triggerToast('error', 'Error de red.');
        }
    };

    const handleDeleteTheme = async (themeId) => {
        if (!window.confirm('¿Seguro que deseas eliminar este tema?')) return;
        try {
            const res = await DeleteTheme(themeId, roomId);
            if (res.success) {
                triggerToast('info', 'Tema eliminado.');
                loadAdminDataRef.current(true);
            } else {
                triggerToast('error', res.message || 'Error al eliminar tema.');
            }
        } catch {
            triggerToast('error', 'Error de red.');
        }
    };

    // -------------------------------------------------------------------------
    // 10. FILTRADO DE TABLA DE PARTICIPANTES
    // -------------------------------------------------------------------------
    const filteredParticipants = useMemo(() => {
        return participants.filter((p) => {
            const matchesSearch =
                !searchTerm.trim() ||
                (p.fullName && p.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (p.phone && p.phone.includes(searchTerm));

            if (!matchesSearch) return false;

            if (statusFilter === 'ALL') return true;
            if (statusFilter === 'COMPLETED') return p.status === 'COMPLETED';
            if (statusFilter === 'CRASHED') return p.status === 'CRASHED';
            if (statusFilter === 'REGISTERED') return p.status === 'REGISTERED';
            return true;
        });
    }, [participants, searchTerm, statusFilter]);

    return (
        <div className="personal-surfer-board-container">
            {/* Header del Operador */}
            <header className="ps-header">
                <div className="ps-header-left">
                    <button type="button" className="ps-back-btn" onClick={getOutOfTheDynamic} title="Regresar al panel principal">
                        <i className="bi bi-arrow-left-circle-fill me-2" />
                        Salir
                    </button>
                    <div className="ps-title-box">
                        <h2 className="ps-main-title">
                            <i className="bi bi-water me-2 text-info" />
                            Esquivador Personal (Surfer)
                        </h2>
                        <span className="ps-badge-id">Dynamic ID: 51</span>
                    </div>
                </div>

                <div className="ps-header-right">
                    <div className={`ps-ws-badge ws-${wsStatus}`}>
                        <span className="ps-ws-dot" />
                        {wsStatus === 'connected' ? 'En Vivo' : wsStatus === 'reconnecting' ? 'Reconectando...' : 'Desconectado'}
                    </div>

                    <button
                        type="button"
                        className="ps-btn ps-btn-refresh"
                        onClick={() => loadAdminData()}
                        disabled={isLoading}
                    >
                        <i className={`bi bi-arrow-clockwise ${isLoading ? 'spin' : ''}`} />
                    </button>
                </div>
            </header>

            {/* Toast Notification */}
            {toast.visible && (
                <div className={`ps-toast toast-${toast.type}`}>
                    <i className={`bi ${toast.type === 'success' ? 'bi-check-circle-fill' : toast.type === 'warning' ? 'bi-exclamation-triangle-fill' : toast.type === 'error' ? 'bi-x-circle-fill' : 'bi-info-circle-fill'} me-2`} />
                    {toast.message}
                </div>
            )}

            {/* Fila Superior de 10 KPIs */}
            <PersonalSurferMetricsRow metrics={metrics} />

            {/* Acordeones de Gestión y Configuración */}
            <div className="accordion ps-accordion mt-3" id="psAccordionGroup">
                {/* Acordeón 1: Control de Live Overlay */}
                <div className="accordion-item ps-accordion-item">
                    <h2 className="accordion-header" id="headingOverlay">
                        <button
                            className="accordion-button collapsed ps-accordion-btn"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseOverlay"
                        >
                            <i className="bi bi-display-fill me-2 text-primary" />
                            1. Control de Live Overlay (Pantalla Gigante / Jumbotron)
                            <span className={`badge ms-3 ${overlayVisible ? 'bg-success' : 'bg-secondary'}`}>
                                {overlayVisible ? 'Visible' : 'Oculto'}
                            </span>
                        </button>
                    </h2>
                    <div id="collapseOverlay" className="accordion-collapse collapse" data-bs-parent="#psAccordionGroup">
                        <form onSubmit={handleSaveOverlay} className="accordion-body ps-accordion-body">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="ps-label">Visibilidad del Overlay</label>
                                    <div className="form-check form-switch ps-switch mt-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={overlayVisible}
                                            onChange={(e) => setOverlayVisible(e.target.checked)}
                                        />
                                        <label className="form-check-label">{overlayVisible ? 'Visible en Estadio' : 'Oculto'}</label>
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <label className="ps-label">Modo de Overlay</label>
                                    <select
                                        className="ps-select"
                                        value={overlayMode}
                                        onChange={(e) => setOverlayMode(e.target.value)}
                                    >
                                        <option value="invitation">Modo Invitación (Códigos QR)</option>
                                        <option value="leaderboard">Modo Leaderboard (Top 10 en Vivo)</option>
                                    </select>
                                </div>

                                <div className="col-md-3">
                                    <label className="ps-label">Cantidad de Códigos QR</label>
                                    <select
                                        className="ps-select"
                                        value={overlayQrCount}
                                        onChange={(e) => setOverlayQrCount(Number(e.target.value))}
                                    >
                                        <option value="1">1 Código QR (Centro)</option>
                                        <option value="2">2 Códigos QR (Extremos)</option>
                                    </select>
                                </div>

                                <div className="col-md-3">
                                    <label className="ps-label">Ajuste de Posición Y (Offset: {overlayOffsetY}px)</label>
                                    <input
                                        type="range"
                                        className="form-range"
                                        min="-300"
                                        max="300"
                                        value={overlayOffsetY}
                                        onChange={(e) => setOverlayOffsetY(Number(e.target.value))}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="ps-label">Banner 1536x864 (Ultra Wide)</label>
                                    <div className="d-flex gap-2">
                                        <input
                                            type="text"
                                            className="ps-input"
                                            value={overlayBanner1536Url}
                                            onChange={(e) => setOverlayBanner1536Url(e.target.value)}
                                            placeholder="URL o sube banner"
                                        />
                                        <label className="ps-upload-btn">
                                            <i className="bi bi-cloud-arrow-up-fill me-1" />
                                            Subir
                                            <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                                onChange={(e) => handleBannerUpload(e, 'banner1536')}
                                            />
                                        </label>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <label className="ps-label">Banner 1920x1080 (Full HD)</label>
                                    <div className="d-flex gap-2">
                                        <input
                                            type="text"
                                            className="ps-input"
                                            value={overlayBanner1920Url}
                                            onChange={(e) => setOverlayBanner1920Url(e.target.value)}
                                            placeholder="URL o sube banner"
                                        />
                                        <label className="ps-upload-btn">
                                            <i className="bi bi-cloud-arrow-up-fill me-1" />
                                            Subir
                                            <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                                onChange={(e) => handleBannerUpload(e, 'banner1920')}
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-3 text-end">
                                <button type="submit" className="ps-btn ps-btn-primary" disabled={isSavingOverlay}>
                                    <i className="bi bi-check2-circle me-1" />
                                    {isSavingOverlay ? 'Guardando...' : 'Guardar Live Overlay'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Acordeón 2: Parámetros del Juego y Anti-Fraude */}
                <div className="accordion-item ps-accordion-item">
                    <h2 className="accordion-header" id="headingConfig">
                        <button
                            className="accordion-button collapsed ps-accordion-btn"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseConfig"
                        >
                            <i className="bi bi-sliders me-2 text-info" />
                            2. Parámetros del Juego y Control de Acceso
                            <span className={`badge ms-3 ${isGameActive ? 'bg-success' : 'bg-danger'}`}>
                                {isGameActive ? 'Juego Abierto' : 'Juego Cerrado'}
                            </span>
                        </button>
                    </h2>
                    <div id="collapseConfig" className="accordion-collapse collapse" data-bs-parent="#psAccordionGroup">
                        <form onSubmit={handleSaveConfig} className="accordion-body ps-accordion-body">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="ps-label">Estado de Acceso</label>
                                    <div className="form-check form-switch ps-switch mt-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={isGameActive}
                                            onChange={(e) => setIsGameActive(e.target.checked)}
                                        />
                                        <label className="form-check-label">{isGameActive ? 'Permitir Jugadas' : 'Cerrar Registro'}</label>
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <label className="ps-label">Criterio de Victoria</label>
                                    <select
                                        className="ps-select"
                                        value={winMode}
                                        onChange={(e) => setWinMode(e.target.value)}
                                    >
                                        <option value="sudden_death">Muerte Súbita (Choque pierde)</option>
                                        <option value="points_deduction">Deducción de Puntos (Completa carrera)</option>
                                    </select>
                                </div>

                                <div className="col-md-3">
                                    <label className="ps-label">Duración de la Carrera</label>
                                    <select
                                        className="ps-select"
                                        value={timeLimitSeconds}
                                        onChange={(e) => setTimeLimitSeconds(Number(e.target.value))}
                                    >
                                        <option value="20">20 Segundos</option>
                                        <option value="30">30 Segundos (Default)</option>
                                        <option value="45">45 Segundos</option>
                                        <option value="60">60 Segundos</option>
                                    </select>
                                </div>

                                <div className="col-md-3">
                                    <label className="ps-label">Tema Activo Obligatorio</label>
                                    <select
                                        className="ps-select"
                                        value={selectedThemeId}
                                        onChange={(e) => setSelectedThemeId(e.target.value)}
                                    >
                                        <option value="">-- Seleccionar Tema Activo --</option>
                                        {themes.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name} {t.id === config?.activeThemeId ? '★ (Activo)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-3">
                                    <label className="ps-label">Puntos por Moneda</label>
                                    <input
                                        type="number"
                                        className="ps-input"
                                        value={pointsPerCoin}
                                        onChange={(e) => setPointsPerCoin(Number(e.target.value))}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <label className="ps-label">Puntos por Bonus</label>
                                    <input
                                        type="number"
                                        className="ps-input"
                                        value={pointsPerBonus}
                                        onChange={(e) => setPointsPerBonus(Number(e.target.value))}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <label className="ps-label">Penalización por Choque</label>
                                    <input
                                        type="number"
                                        className="ps-input"
                                        value={penaltyPerHit}
                                        onChange={(e) => setPenaltyPerHit(Number(e.target.value))}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <label className="ps-label">Permitir Reintentos en Mismo Móvil</label>
                                    <div className="form-check form-switch ps-switch mt-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={allowSharedDevice}
                                            onChange={(e) => setAllowSharedDevice(e.target.checked)}
                                        />
                                        <label className="form-check-label">{allowSharedDevice ? 'Permitido' : 'Solo 1 por Teléfono'}</label>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-3 text-end">
                                <button type="submit" className="ps-btn ps-btn-primary" disabled={isSavingConfig}>
                                    <i className="bi bi-check2-circle me-1" />
                                    {isSavingConfig ? 'Guardando...' : 'Guardar Parámetros de Juego'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Acordeón 3: Gestor de Temas Visuales */}
                <div className="accordion-item ps-accordion-item">
                    <h2 className="accordion-header" id="headingThemes">
                        <button
                            className="accordion-button collapsed ps-accordion-btn"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseThemes"
                        >
                            <i className="bi bi-palette-fill me-2 text-warning" />
                            3. Gestor de Temas Visuales ({themes.length} creados)
                        </button>
                    </h2>
                    <div id="collapseThemes" className="accordion-collapse collapse" data-bs-parent="#psAccordionGroup">
                        <div className="accordion-body ps-accordion-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <p className="text-muted mb-0">Crea o personaliza los temas visuales del juego (fondos, pista, obstáculos, monedas y personajes).</p>
                                <button
                                    type="button"
                                    className="ps-btn ps-btn-primary"
                                    onClick={() => {
                                        setEditingTheme(null);
                                        setIsThemeModalOpen(true);
                                    }}
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    Crear Nuevo Tema
                                </button>
                            </div>

                            {themes.length === 0 ? (
                                <div className="ps-empty-box">
                                    <i className="bi bi-palette text-muted fs-1 mb-2" />
                                    <p className="mb-0">No hay temas creados para esta sala. Crea uno para poder iniciar la dinámica.</p>
                                </div>
                            ) : (
                                <div className="row g-3">
                                    {themes.map((t) => {
                                        const isActive = t.id === config?.activeThemeId;
                                        return (
                                            <div key={t.id} className="col-md-4">
                                                <div className={`ps-theme-card ${isActive ? 'active-theme' : ''}`}>
                                                    <div className="ps-theme-card-header">
                                                        <h5 className="mb-0">{t.name}</h5>
                                                        {isActive && <span className="badge bg-success">Activo</span>}
                                                    </div>

                                                    <div className="ps-theme-colors my-2">
                                                        <span className="ps-color-badge" style={{ backgroundColor: t.primaryColor || '#00E5FF' }}>
                                                            Primario
                                                        </span>
                                                        <span className="ps-color-badge" style={{ backgroundColor: t.secondaryColor || '#FF0055' }}>
                                                            Secundario
                                                        </span>
                                                    </div>

                                                    <div className="ps-theme-actions mt-3">
                                                        {!isActive && (
                                                            <button
                                                                type="button"
                                                                className="ps-btn ps-btn-sm ps-btn-success"
                                                                onClick={() => handleActivateTheme(t.id)}
                                                            >
                                                                <i className="bi bi-check-lg me-1" />
                                                                Activar
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="ps-btn ps-btn-sm ps-btn-secondary"
                                                            onClick={() => {
                                                                setEditingTheme(t);
                                                                setIsThemeModalOpen(true);
                                                            }}
                                                        >
                                                            <i className="bi bi-pencil-fill me-1" />
                                                            Editar
                                                        </button>
                                                        {!isActive && (
                                                            <button
                                                                type="button"
                                                                className="ps-btn ps-btn-sm ps-btn-danger"
                                                                onClick={() => handleDeleteTheme(t.id)}
                                                            >
                                                                <i className="bi bi-trash-fill" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Acordeón 4: Zona Peligrosa */}
                <div className="accordion-item ps-accordion-item ps-accordion-danger">
                    <h2 className="accordion-header" id="headingDanger">
                        <button
                            className="accordion-button collapsed ps-accordion-btn"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseDanger"
                        >
                            <i className="bi bi-exclamation-octagon-fill me-2 text-danger" />
                            4. Zona Peligrosa (Danger Zone - Reinicio Maestro)
                        </button>
                    </h2>
                    <div id="collapseDanger" className="accordion-collapse collapse" data-bs-parent="#psAccordionGroup">
                        <div className="accordion-body ps-accordion-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="text-danger mb-1">Reinicio Maestro de Sala</h5>
                                    <p className="text-muted mb-0">Archiva todos los participantes, limpia el leaderboard y restablece las métricas a 0.</p>
                                </div>
                                <button
                                    type="button"
                                    className="ps-btn ps-btn-danger"
                                    onClick={() => setIsDangerModalOpen(true)}
                                >
                                    <i className="bi bi-trash-fill me-1" />
                                    Abrir Modal de Reinicio
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabla Táctil de Participantes y Leads */}
            <div className="ps-participants-section mt-4">
                <div className="ps-table-header">
                    <div className="d-flex align-items-center gap-3">
                        <h4 className="mb-0">
                            <i className="bi bi-people-fill me-2 text-info" />
                            Participantes y Leads ({filteredParticipants.length})
                        </h4>
                        <div className="form-check form-switch ps-switch ms-3">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={hidePii}
                                onChange={(e) => setHidePii(e.target.checked)}
                            />
                            <label className="form-check-label text-muted">
                                <i className={`bi ${hidePii ? 'bi-shield-lock-fill' : 'bi-shield-slash-fill'} me-1`} />
                                {hidePii ? 'Protección PII Activa' : 'Mostrar Teléfonos'}
                            </label>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        {/* Buscador */}
                        <div className="ps-search-box">
                            <i className="bi bi-search ps-search-icon" />
                            <input
                                type="text"
                                className="ps-search-input"
                                placeholder="Buscar por nombre o teléfono..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Filtros rápidos */}
                        <select
                            className="ps-select ps-select-sm"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="ALL">Todos los Estados</option>
                            <option value="COMPLETED">Completados</option>
                            <option value="CRASHED">Chocados / Eliminados</option>
                            <option value="REGISTERED">Registrados</option>
                        </select>

                        {/* Botón Exportar a Excel */}
                        <a
                            href={getExportExcelUrl(roomId)}
                            className="ps-btn ps-btn-success"
                            download
                            title="Descargar base de datos en Excel"
                        >
                            <i className="bi bi-file-earmark-excel-fill me-1" />
                            Exportar a Excel (.xlsx)
                        </a>
                    </div>
                </div>

                {/* Tabla de Datos */}
                <div className="ps-table-container mt-2">
                    <table className="ps-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Nombre Completo</th>
                                <th>Teléfono / WhatsApp</th>
                                <th>Puntuación</th>
                                <th>Impactos</th>
                                <th>Monedas</th>
                                <th>Bonus</th>
                                <th>Tiempo</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredParticipants.length === 0 ? (
                                <tr>
                                    <td colSpan="10" className="text-center py-4 text-muted">
                                        No se encontraron registros que coincidan con la búsqueda.
                                    </td>
                                </tr>
                            ) : (
                                filteredParticipants.map((p, idx) => {
                                    const displayPhone = hidePii ? maskPhoneNumber(p.phone) : p.phone;
                                    return (
                                        <tr key={p.id}>
                                            <td>{idx + 1}</td>
                                            <td>
                                                <strong>{p.fullName}</strong>
                                                {p.isWinner && <span className="badge bg-warning text-dark ms-2">Top 3</span>}
                                            </td>
                                            <td>
                                                <code>{displayPhone}</code>
                                            </td>
                                            <td>
                                                <span className="ps-score-badge">{p.score?.toLocaleString('es-MX') || 0} pts</span>
                                            </td>
                                            <td>{p.hitsCount || 0}</td>
                                            <td>{p.coinsCollected || 0}</td>
                                            <td>{p.bonusCollected || 0}</td>
                                            <td>{p.timePlayedSeconds ? `${p.timePlayedSeconds}s` : '—'}</td>
                                            <td>
                                                <span className={`badge ${p.status === 'COMPLETED' ? 'bg-success' : p.status === 'CRASHED' ? 'bg-danger' : 'bg-secondary'}`}>
                                                    {p.status}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="ps-btn ps-btn-sm ps-btn-warning"
                                                    onClick={() => {
                                                        setResetTargetParticipant(p);
                                                        setIsResetModalOpen(true);
                                                    }}
                                                    title="Dar otra oportunidad al aficionado"
                                                >
                                                    <i className="bi bi-arrow-counterclockwise me-1" />
                                                    Reintentar
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modales */}
            <PersonalSurferThemeModal
                isOpen={isThemeModalOpen}
                onClose={() => setIsThemeModalOpen(false)}
                theme={editingTheme}
                roomId={roomId}
                onSaved={() => loadAdminDataRef.current(true)}
                triggerToast={triggerToast}
            />

            <PersonalSurferResetModal
                isOpen={isResetModalOpen}
                onClose={() => setIsResetModalOpen(false)}
                participant={resetTargetParticipant}
                roomId={roomId}
                onSuccess={() => loadAdminDataRef.current(true)}
                triggerToast={triggerToast}
            />

            <PersonalSurferDangerModal
                isOpen={isDangerModalOpen}
                onClose={() => setIsDangerModalOpen(false)}
                roomId={roomId}
                onResetCompleted={() => loadAdminDataRef.current(true)}
                triggerToast={triggerToast}
            />
        </div>
    );
};

PersonalSurferBoard.propTypes = {
    getOutOfTheDynamic: PropTypes.func,
    roomId: PropTypes.string
};
